// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// The composer's single primary button. Empty input offers a voice conversation
// (soundwave glyph — deliberately not a mic, which reads as dictation); typed input
// becomes Send. The two icons crossfade in place so the slot never jumps.

import { useTranslation } from 'react-i18next'
import { AudioLines, ArrowUp } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ComposerButtonProps {
  hasText: boolean
  voiceAvailable: boolean
  onSend: () => void
  onVoice: () => void
}

export function ComposerButton({ hasText, voiceAvailable, onSend, onVoice }: ComposerButtonProps) {
  const { t } = useTranslation()
  const showVoice = !hasText && voiceAvailable
  const disabled = !hasText && !voiceAvailable

  return (
    <button
      type="button"
      onClick={showVoice ? onVoice : onSend}
      disabled={disabled}
      aria-label={showVoice ? t('voice.composerVoiceLabel') : t('chat.send')}
      className={cn(
        'relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden',
        'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
        hasText
          ? 'bg-gradient-to-br from-[#006EF9] to-[#02C7C3] text-white hover:opacity-90 shadow-sm shadow-primary/30'
          : voiceAvailable
            ? 'bg-primary/10 text-primary border border-primary/25 hover:bg-primary/15'
            : 'bg-muted text-muted-foreground cursor-not-allowed',
      )}
    >
      <ArrowUp
        aria-hidden
        className={cn(
          'w-5 h-5 absolute transition-all duration-200 motion-reduce:transition-none',
          showVoice ? 'opacity-0 scale-75' : 'opacity-100 scale-100',
        )}
      />
      <AudioLines
        aria-hidden
        className={cn(
          'w-[18px] h-[18px] absolute transition-all duration-200 motion-reduce:transition-none',
          showVoice ? 'opacity-100 scale-100' : 'opacity-0 scale-75',
        )}
      />
    </button>
  )
}
